import styled from "styled-components";
import useAttendanceManage from "features/teacher/hooks/lesson/useAttendanceManage";
import { useState } from "react";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface AttendanceManageProps {
    lessonId: string;
}

interface SortConfig {
    key: string;
    direction: "asc" | "desc";
}

const AttendanceManage = ({ lessonId }: AttendanceManageProps) => {
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: "name", direction: "asc" });
    const { searchedStudents, search, onChange, date, onChangeDate, saveAttendance, onTodayClick, attendanceCount } = useAttendanceManage(lessonId);

    const onSort = (key: string) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const sortedStudents = [...searchedStudents].sort((a, b) => {
        if (sortConfig.key === "name") {
            return sortConfig.direction === "asc"
                ? a.name.localeCompare(b.name)
                : b.name.localeCompare(a.name);
        }
        return 0;
    });

    return (
        <Panel>
            <Toolbar>
                <SearchInput
                    type="text"
                    placeholder="학생 검색"
                    value={search}
                    onChange={onChange}
                />
                <DateWrap>
                    <Label htmlFor="attendance-date">날짜</Label>
                    <DateInput id="attendance-date" type="date" value={date} onChange={onChangeDate} />
                    <TodayButton type="button" onClick={onTodayClick}>오늘</TodayButton>
                </DateWrap>
                <Counts>
                    <CountItem>
                        <CountLabel>전체</CountLabel>
                        <CountValue>{attendanceCount.all}</CountValue>
                    </CountItem>
                    <CountItem $tone="ok">
                        <CountLabel>출석</CountLabel>
                        <CountValue>{attendanceCount.attend}</CountValue>
                    </CountItem>
                    <CountItem $tone="warn">
                        <CountLabel>지각</CountLabel>
                        <CountValue>{attendanceCount.tardy}</CountValue>
                    </CountItem>
                    <CountItem $tone="bad">
                        <CountLabel>결석</CountLabel>
                        <CountValue>{attendanceCount.absent}</CountValue>
                    </CountItem>
                </Counts>
            </Toolbar>
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <Th
                                onClick={() => onSort("name")}
                                $sortable
                            >
                                학생 이름 {sortConfig.key === "name" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                            </Th>
                            <Th>상태</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedStudents.length === 0 ? (
                            <tr>
                                <EmptyTd colSpan={2}>출결을 기록할 학생이 없습니다.</EmptyTd>
                            </tr>
                        ) : (
                            sortedStudents.map((student) => (
                                <tr key={student.uid}>
                                    <Td>{student.name}</Td>
                                    <Td>
                                        <ButtonGroup>
                                            <SmallButton
                                                type="button"
                                                onClick={() => saveAttendance("attend", student.uid)}
                                                $on={student.state === "attend"}
                                            >
                                                출석
                                            </SmallButton>
                                            <SmallButton
                                                type="button"
                                                onClick={() => saveAttendance("absent", student.uid)}
                                                $on={student.state === "absent"}
                                                $tone="bad"
                                            >
                                                결석
                                            </SmallButton>
                                            <SmallButton
                                                type="button"
                                                onClick={() => saveAttendance("tardy", student.uid)}
                                                $on={student.state === "tardy"}
                                                $tone="warn"
                                            >
                                                지각
                                            </SmallButton>
                                        </ButtonGroup>
                                    </Td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </TableWrap>
        </Panel>
    );
};

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    flex: 1;
    min-height: 0;
    padding: 18px 20px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Toolbar = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
`;

const SearchInput = styled.input`
    width: 200px;
    padding: 9px 12px;
    border: 1px solid ${hw.border};
    border-radius: 8px;
    font-size: 13px;
    color: ${hw.text};

    &:focus {
        outline: none;
        border-color: ${hw.brand};
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }

    &::placeholder {
        color: ${hw.textSubtle};
    }
`;

const DateWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: ${hw.bg};
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid ${hw.border};
`;

const DateInput = styled.input`
    padding: 6px 8px;
    border: 1px solid ${hw.border};
    border-radius: 6px;
    font-size: 13px;
    color: ${hw.text};

    &:focus {
        outline: none;
        border-color: ${hw.brand};
    }
`;

const Label = styled.label`
    color: ${hw.textMuted};
    font-size: 12px;
    font-weight: 600;
`;

const TodayButton = styled.button`
    appearance: none;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    color: ${hw.text};
    font-size: 12px;
    font-weight: 600;
    border-radius: 6px;
    padding: 6px 10px;
    cursor: pointer;

    &:hover {
        background: ${hw.brandSoft};
        border-color: ${hw.brandMuted};
        color: ${hw.brand};
    }
`;

const Counts = styled.div`
    display: flex;
    gap: 8px;
    margin-left: auto;
`;

const CountItem = styled.div<{ $tone?: "ok" | "warn" | "bad" }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-width: 56px;
    padding: 6px 10px;
    background: ${props =>
        props.$tone === "ok" ? "#F0FDF4"
            : props.$tone === "warn" ? "#FFFBEB"
                : props.$tone === "bad" ? hw.brandSoft
                    : hw.bg};
    border: 1px solid ${hw.border};
    border-radius: 8px;
`;

const CountLabel = styled.span`
    color: ${hw.textMuted};
    font-size: 11px;
    font-weight: 600;
`;

const CountValue = styled.span`
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
`;

const TableWrap = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    border: 1px solid ${hw.border};
    border-radius: 12px;
    ${scrollbar}
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const Th = styled.th<{ $sortable?: boolean }>`
    padding: 12px 14px;
    color: ${hw.textMuted};
    font-size: 13px;
    font-weight: 700;
    text-align: left;
    background: #FAFBFC;
    border-bottom: 1px solid ${hw.border};
    position: sticky;
    top: 0;
    cursor: ${props => props.$sortable ? "pointer" : "default"};
`;

const Td = styled.td`
    padding: 10px 14px;
    color: ${hw.text};
    font-size: 14px;
    font-weight: 500;
    border-bottom: 1px solid ${hw.border};
`;

const EmptyTd = styled.td`
    padding: 48px 16px;
    text-align: center;
    color: ${hw.textSubtle};
    font-size: 13px;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 6px;
`;

const SmallButton = styled.button<{ $on?: boolean; $tone?: "warn" | "bad" }>`
    appearance: none;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    border: 1px solid ${props => {
        if (!props.$on) return hw.border;
        if (props.$tone === "bad") return hw.brand;
        if (props.$tone === "warn") return "#D97706";
        return hw.success;
    }};
    background: ${props => {
        if (!props.$on) return hw.surface;
        if (props.$tone === "bad") return hw.brand;
        if (props.$tone === "warn") return "#D97706";
        return hw.success;
    }};
    color: ${props => props.$on ? "#fff" : hw.textMuted};

    &:hover {
        background: ${props => props.$on ? undefined : hw.bg};
    }
`;

export default AttendanceManage;
